import React, { useState } from 'react';
import styled from 'styled-components';
import { IconButton, PrimaryButton } from '../../Button';
import VipIcon from '../../../Icons/VipIcons';
import { VIDEO_THUMBNAIL } from '../../../../constants/staticTexts';

/**
 * VideoThumbnailSelector
 * Allows users to select which video preview frame to use as the thumbnail
 * Shows multiple frames extracted from the video at different timestamps
 */
const VideoThumbnailSelector = ({ 
  thumbnails = [], 
  selectedIndex = 0, 
  onSelect,
  onClose 
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!thumbnails || thumbnails.length === 0) {
    return null;
  }

  const formatTimestamp = (ms) => {
    if (!ms) return '';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <SelectorOverlay onClick={onClose}>
      <SelectorModal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <Title>
            <VipIcon name="VideoIcon" size={20} />
            {VIDEO_THUMBNAIL.TITLE}
          </Title>
          <CloseButton aria-label="Close" onClick={onClose}>
            <VipIcon name="CloseXCircle" size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Instructions>
            {VIDEO_THUMBNAIL.INSTRUCTIONS}
          </Instructions>

          <ThumbnailGrid>
            {thumbnails.map((thumb, index) => (
              <ThumbnailOption
                key={thumb.s3Key || index}
                $isSelected={index === selectedIndex}
                $isHovered={index === hoveredIndex}
                onClick={() => onSelect(index)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <ThumbnailImage src={thumb.url} alt={`${VIDEO_THUMBNAIL.FRAME_PREFIX} ${index + 1}`} />
                {thumb.timestamp && (
                  <TimestampBadge>{formatTimestamp(thumb.timestamp)}</TimestampBadge>
                )}
                {index === selectedIndex && (
                  <SelectedBadge>
                    <VipIcon name="CheckCircle" size={16} />
                  </SelectedBadge>
                )}
                <FrameNumber>{VIDEO_THUMBNAIL.FRAME_PREFIX} {index + 1}</FrameNumber>
              </ThumbnailOption>
            ))}
          </ThumbnailGrid>
        </ModalBody>

        <ModalFooter>
          <FooterText>
            {thumbnails.length} {VIDEO_THUMBNAIL.FRAMES_AVAILABLE_SUFFIX}
          </FooterText>
          <PrimaryButton onClick={onClose}>
            {VIDEO_THUMBNAIL.DONE_BUTTON}
          </PrimaryButton>
        </ModalFooter>
      </SelectorModal>
    </SelectorOverlay>
  );
};

// Styled Components
const SelectorOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const SelectorModal = styled.div`
  background: ${({ theme }) => theme.colors.cardBg};
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CloseButton = styled(IconButton).attrs({ $size: 'small' })`
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
`;

const Instructions = styled.p`
  margin: 0 0 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  text-align: center;
`;

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
`;

const ThumbnailOption = styled.div`
  position: relative;
  aspect-ratio: 3/4;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid ${({ theme, $isSelected }) => 
    $isSelected ? theme.colors.primary : 'transparent'};
  transition: all 0.2s ease;
  transform: ${({ $isHovered }) => $isHovered ? 'scale(1.02)' : 'none'};
  
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TimestampBadge = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 6px;
  border-radius: 4px;
  font-family: monospace;
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FrameNumber = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const FooterText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
`;

export default VideoThumbnailSelector;
